import type {
  ResearchResource,
  Researcher,
  Institution,
  Workspace,
  SearchHistoryItem,
  Citation,
  QualityAssuranceItem,
  ResearchDashboardStats,
  ListResourcesParams,
  ListResearchersParams,
  ListInstitutionsParams,
  ImageSearchResult,
  ResearchSettings,
  ResourceType,
  ANZSRCCode,
} from '@/types/research';

// ============================================================================
// SAMPLE DATA - Comprehensive Australian Research Data Center
// ============================================================================

const SAMPLE_RESOURCES: ResearchResource[] = [
  {
    id: 'res-001',
    tenantId: 'tenant-001',
    title: 'Climate Change Impact on Great Barrier Reef Coral Bleaching Patterns 2020-2024',
    description: 'Comprehensive dataset analyzing coral bleaching events across 450 reef sites with temperature and pH measurements.',
    abstract: 'This study presents a longitudinal analysis of coral bleaching patterns across the Great Barrier Reef Marine Park, correlating bleaching events with sea surface temperature anomalies, ocean acidification levels, and cyclone activity over a 4-year period.',
    type: 'dataset',
    status: 'published',
    accessLevel: 'public',
    authors: ['Dr. Sarah Mitchell', 'Prof. James Chen', 'Dr. Emma Thompson'],
    institutions: ['Australian Institute of Marine Science', 'James Cook University'],
    keywords: ['climate change', 'coral bleaching', 'Great Barrier Reef', 'marine ecology', 'ocean temperature'],
    anzsrcCodes: ['05 - Environmental Sciences', '06 - Biological Sciences'],
    methodology: 'Remote sensing analysis combined with in-situ measurements using autonomous underwater vehicles',
    grantNumber: 'ARC-DP210103456',
    fundingSource: 'Australian Research Council',
    doi: '10.1234/reef.2024.001',
    fileUrl: '/files/reef-dataset-2024.zip',
    fileSize: 2500000000,
    fileName: 'reef-dataset-2024.zip',
    thumbnailUrl: '/thumbnails/reef-chart.png',
    publicationDate: '2024-03-15',
    createdAt: '2024-01-10T08:00:00Z',
    updatedAt: '2024-03-15T14:30:00Z',
    citationCount: 47,
    downloadCount: 1234,
    viewCount: 5678,
    relatedResourceIds: ['res-002', 'res-005'],
    citedByIds: ['res-012', 'res-015'],
    citesIds: ['res-003'],
  },
  {
    id: 'res-002',
    tenantId: 'tenant-001',
    title: 'Machine Learning Framework for Astronomical Object Classification',
    description: 'Open-source software for automated classification of stellar objects using deep learning neural networks.',
    type: 'software',
    status: 'published',
    accessLevel: 'public',
    authors: ['Dr. Michael Wang', 'Dr. Lisa Anderson'],
    institutions: ['Swinburne University of Technology', 'Australian National University'],
    keywords: ['machine learning', 'astronomy', 'deep learning', 'neural networks', 'stellar classification'],
    anzsrcCodes: ['02 - Physical Sciences', '08 - Information and Computing Sciences'],
    methodology: 'Convolutional neural networks trained on SKA pathfinder data',
    grantNumber: 'ARC-LE200100078',
    fundingSource: 'Australian Research Council',
    doi: '10.5281/zenodo.7654321',
    fileUrl: 'https://github.com/astro-ml/stellar-classifier',
    createdAt: '2023-11-20T10:00:00Z',
    updatedAt: '2024-02-28T09:15:00Z',
    citationCount: 89,
    downloadCount: 3456,
    viewCount: 12890,
    relatedResourceIds: ['res-001'],
    citedByIds: ['res-018'],
    citesIds: [],
  },
  {
    id: 'res-003',
    tenantId: 'tenant-001',
    title: 'Indigenous Land Management Practices and Bushfire Prevention: A 50-Year Analysis',
    description: 'Peer-reviewed article examining traditional Aboriginal fire management techniques and their efficacy in reducing bushfire intensity.',
    type: 'article',
    status: 'published',
    accessLevel: 'institution',
    authors: ['Prof. David Williams', 'Dr. Mary Nampijinpa', 'Dr. Robert Taylor'],
    institutions: ['Charles Darwin University', 'CSIRO'],
    keywords: ['indigenous knowledge', 'bushfire management', 'land management', 'cultural burning', 'fire ecology'],
    anzsrcCodes: ['05 - Environmental Sciences', '16 - Studies in Human Society'],
    methodology: 'Mixed methods combining satellite imagery analysis with traditional knowledge documentation',
    grantNumber: 'NHMRC-GNT1187654',
    fundingSource: 'National Health and Medical Research Council',
    doi: '10.1038/nature.2024.5678',
    publicationDate: '2024-01-22',
    createdAt: '2023-08-15T14:00:00Z',
    updatedAt: '2024-01-22T16:45:00Z',
    citationCount: 156,
    downloadCount: 892,
    viewCount: 4523,
    relatedResourceIds: ['res-008'],
    citedByIds: ['res-001'],
    citesIds: [],
  },
  {
    id: 'res-004',
    tenantId: 'tenant-001',
    title: 'CRISPR Gene Editing Protocol for Wheat Rust Resistance',
    description: 'Laboratory protocol and supporting materials for developing rust-resistant wheat varieties using CRISPR-Cas9.',
    type: 'manual',
    status: 'published',
    accessLevel: 'team',
    authors: ['Dr. Helen Zhang', 'Prof. Andrew Brown'],
    institutions: ['CSIRO Agriculture', 'University of Adelaide'],
    keywords: ['CRISPR', 'gene editing', 'wheat', 'rust resistance', 'agriculture'],
    anzsrcCodes: ['06 - Biological Sciences', '07 - Agricultural and Veterinary Sciences'],
    methodology: 'CRISPR-Cas9 gene editing with agrobacterium-mediated transformation',
    grantNumber: 'GRDC-DAW2003-002',
    fundingSource: 'Grains Research and Development Corporation',
    createdAt: '2023-06-01T09:00:00Z',
    updatedAt: '2024-04-10T11:20:00Z',
    citationCount: 34,
    downloadCount: 567,
    viewCount: 2341,
    relatedResourceIds: [],
    citedByIds: [],
    citesIds: [],
  },
  {
    id: 'res-005',
    tenantId: 'tenant-001',
    title: 'Quantum Computing Algorithm for Drug Discovery Optimization',
    description: 'Patent application for novel quantum algorithm accelerating molecular simulation in pharmaceutical research.',
    type: 'patent',
    status: 'published',
    accessLevel: 'public',
    authors: ['Prof. Richard Lee', 'Dr. Jessica Martin'],
    institutions: ['University of New South Wales', 'Pawsey Supercomputing Centre'],
    keywords: ['quantum computing', 'drug discovery', 'molecular simulation', 'pharmaceutical', 'optimization'],
    anzsrcCodes: ['03 - Chemical Sciences', '08 - Information and Computing Sciences', '11 - Medical and Health Sciences'],
    grantNumber: 'ARC-FL200100123',
    fundingSource: 'Australian Research Council',
    doi: 'AU2024/123456',
    publicationDate: '2024-02-01',
    createdAt: '2023-09-15T13:00:00Z',
    updatedAt: '2024-02-01T10:00:00Z',
    citationCount: 12,
    downloadCount: 234,
    viewCount: 1567,
    relatedResourceIds: ['res-002'],
    citedByIds: [],
    citesIds: [],
  },
  {
    id: 'res-006',
    tenantId: 'tenant-001',
    title: 'Australian Urban Heat Island Effect: Multi-City Temperature Analysis',
    description: 'High-resolution thermal imaging dataset from 12 major Australian cities measuring urban heat island intensity.',
    type: 'dataset',
    status: 'published',
    accessLevel: 'public',
    authors: ['Dr. Karen White', 'Dr. Thomas Green', 'Prof. Susan Black'],
    institutions: ['University of Melbourne', 'Monash University', 'Bureau of Meteorology'],
    keywords: ['urban heat island', 'thermal imaging', 'climate', 'urban planning', 'temperature'],
    anzsrcCodes: ['04 - Earth Sciences', '05 - Environmental Sciences', '12 - Built Environment and Design'],
    methodology: 'Satellite thermal imaging combined with ground-based sensor networks',
    grantNumber: 'CRC-LCL2022-045',
    fundingSource: 'Cooperative Research Centre for Low Carbon Living',
    doi: '10.5067/MODIS/MYD11A1.006',
    publicationDate: '2023-12-10',
    createdAt: '2023-04-20T11:00:00Z',
    updatedAt: '2023-12-10T15:30:00Z',
    citationCount: 78,
    downloadCount: 2156,
    viewCount: 8934,
    relatedResourceIds: ['res-003'],
    citedByIds: [],
    citesIds: [],
  },
  {
    id: 'res-007',
    tenantId: 'tenant-001',
    title: 'Neural Network Architecture for Australian Sign Language Recognition',
    description: 'Deep learning model trained on 50,000+ Auslan signs for real-time translation applications.',
    type: 'software',
    status: 'published',
    accessLevel: 'public',
    authors: ['Dr. Amanda Foster', 'Dr. Peter Chang'],
    institutions: ['Macquarie University', 'Deaf Australia'],
    keywords: ['sign language', 'Auslan', 'neural networks', 'accessibility', 'machine learning'],
    anzsrcCodes: ['08 - Information and Computing Sciences', '17 - Psychology and Cognitive Sciences'],
    methodology: 'Transformer-based architecture with attention mechanisms for temporal sign recognition',
    grantNumber: 'NDIA-2023-RES-089',
    fundingSource: 'National Disability Insurance Agency',
    fileUrl: 'https://github.com/auslan-ai/recognition-model',
    createdAt: '2023-07-15T08:30:00Z',
    updatedAt: '2024-03-01T14:00:00Z',
    citationCount: 23,
    downloadCount: 1890,
    viewCount: 6721,
    relatedResourceIds: [],
    citedByIds: [],
    citesIds: [],
  },
  {
    id: 'res-008',
    tenantId: 'tenant-001',
    title: 'Renewable Energy Grid Integration: South Australia Case Study',
    description: 'Comprehensive report on challenges and solutions for integrating high-penetration renewable energy into electrical grids.',
    type: 'report',
    status: 'published',
    accessLevel: 'public',
    authors: ['Prof. Mark Johnson', 'Dr. Linda Chen', 'Dr. Steve Miller'],
    institutions: ['University of South Australia', 'SA Power Networks', 'ARENA'],
    keywords: ['renewable energy', 'grid integration', 'solar', 'wind', 'energy storage'],
    anzsrcCodes: ['09 - Engineering', '10 - Technology'],
    methodology: 'Power systems modelling with real-world grid data analysis',
    grantNumber: 'ARENA-2022-GI-012',
    fundingSource: 'Australian Renewable Energy Agency',
    publicationDate: '2024-01-05',
    createdAt: '2023-05-10T10:00:00Z',
    updatedAt: '2024-01-05T12:00:00Z',
    citationCount: 67,
    downloadCount: 3421,
    viewCount: 11234,
    relatedResourceIds: ['res-003'],
    citedByIds: [],
    citesIds: [],
  },
  {
    id: 'res-009',
    tenantId: 'tenant-001',
    title: 'PhD Thesis: Microplastic Distribution in Australian Coastal Waters',
    description: 'Doctoral thesis examining microplastic concentration, composition, and sources across 200 Australian coastal sites.',
    type: 'thesis',
    status: 'embargoed',
    accessLevel: 'institution',
    embargoEndDate: '2025-06-01',
    authors: ['Dr. Rachel Kim'],
    institutions: ['University of Queensland'],
    keywords: ['microplastics', 'marine pollution', 'coastal waters', 'environmental science'],
    anzsrcCodes: ['05 - Environmental Sciences', '03 - Chemical Sciences'],
    methodology: 'FTIR spectroscopy and microscopy analysis of water and sediment samples',
    grantNumber: 'APA-2020-3456',
    fundingSource: 'Australian Postgraduate Award',
    publicationDate: '2024-02-15',
    createdAt: '2024-02-15T09:00:00Z',
    updatedAt: '2024-02-15T09:00:00Z',
    citationCount: 5,
    downloadCount: 45,
    viewCount: 234,
    relatedResourceIds: ['res-001'],
    citedByIds: [],
    citesIds: [],
  },
  {
    id: 'res-010',
    tenantId: 'tenant-001',
    title: 'High-Resolution Electron Microscopy Images: Australian Native Bee Species',
    description: 'Collection of 2,500+ SEM images documenting morphological features of 150 Australian native bee species.',
    type: 'image',
    status: 'published',
    accessLevel: 'public',
    authors: ['Dr. Paul Robinson', 'Dr. Kate Sullivan'],
    institutions: ['Australian Museum', 'University of Sydney'],
    keywords: ['electron microscopy', 'bees', 'entomology', 'biodiversity', 'taxonomy'],
    anzsrcCodes: ['06 - Biological Sciences'],
    methodology: 'Scanning electron microscopy with gold sputter coating',
    grantNumber: 'ABRS-212-27',
    fundingSource: 'Australian Biological Resources Study',
    thumbnailUrl: '/thumbnails/bee-sem.jpg',
    publicationDate: '2023-09-20',
    createdAt: '2023-03-01T14:00:00Z',
    updatedAt: '2023-09-20T16:30:00Z',
    citationCount: 19,
    downloadCount: 678,
    viewCount: 3456,
    relatedResourceIds: [],
    citedByIds: [],
    citesIds: [],
  },
  {
    id: 'res-011',
    tenantId: 'tenant-001',
    title: 'Australian Research Infrastructure Investment Strategy 2025-2035',
    description: 'Strategic proposal outlining national priorities for research infrastructure development and funding allocation.',
    type: 'grant_proposal',
    status: 'under_review',
    accessLevel: 'team',
    authors: ['Prof. Elizabeth Harper', 'Dr. John Matthews', 'Prof. William Davis'],
    institutions: ['Australian Academy of Science', 'NCRIS'],
    keywords: ['research infrastructure', 'strategic planning', 'funding', 'national priorities'],
    anzsrcCodes: ['13 - Education'],
    createdAt: '2024-04-01T08:00:00Z',
    updatedAt: '2024-04-15T10:00:00Z',
    citationCount: 0,
    downloadCount: 12,
    viewCount: 89,
    relatedResourceIds: [],
    citedByIds: [],
    citesIds: [],
  },
  {
    id: 'res-012',
    tenantId: 'tenant-001',
    title: 'Antarctic Ice Core Data: 800,000 Year Climate Record',
    description: 'Comprehensive dataset from Law Dome ice cores providing atmospheric CO2 and temperature proxy data.',
    type: 'dataset',
    status: 'published',
    accessLevel: 'public',
    authors: ['Prof. Catherine Moore', 'Dr. Ian Stewart', 'Dr. Julie Adams'],
    institutions: ['Australian Antarctic Division', 'University of Tasmania'],
    keywords: ['ice cores', 'paleoclimate', 'Antarctica', 'CO2', 'climate history'],
    anzsrcCodes: ['04 - Earth Sciences', '05 - Environmental Sciences'],
    methodology: 'Ion chromatography and mass spectrometry analysis of ice core samples',
    grantNumber: 'AAS-4506',
    fundingSource: 'Australian Antarctic Science Program',
    doi: '10.26179/5f5a0c2c6d7e8',
    publicationDate: '2023-08-01',
    createdAt: '2022-12-01T11:00:00Z',
    updatedAt: '2023-08-01T09:00:00Z',
    citationCount: 234,
    downloadCount: 4567,
    viewCount: 15678,
    relatedResourceIds: ['res-001', 'res-006'],
    citedByIds: [],
    citesIds: ['res-001'],
  },
];

const SAMPLE_RESEARCHERS: Researcher[] = [
  {
    id: 'researcher-001',
    tenantId: 'tenant-001',
    firstName: 'Sarah',
    lastName: 'Mitchell',
    email: 's.mitchell@aims.gov.au',
    profileImageUrl: 'https://ui-avatars.com/api/?name=Sarah+Mitchell&background=667eea&color=fff',
    title: 'Dr.',
    position: 'Senior Research Scientist',
    department: 'Coral Reef Studies',
    institutionId: 'inst-001',
    institutionName: 'Australian Institute of Marine Science',
    orcidId: '0000-0002-1234-5678',
    expertise: ['Coral Reef Ecology', 'Climate Change Biology', 'Marine Conservation'],
    researchInterests: ['Coral bleaching', 'Ocean acidification', 'Reef restoration'],
    anzsrcCodes: ['05 - Environmental Sciences', '06 - Biological Sciences'],
    publicationCount: 87,
    citationCount: 4521,
    hIndex: 38,
    i10Index: 62,
    isActive: true,
    lastActiveAt: '2024-04-15T14:30:00Z',
    createdAt: '2018-03-01T00:00:00Z',
    updatedAt: '2024-04-15T14:30:00Z',
    collaboratorIds: ['researcher-002', 'researcher-003'],
    resourceIds: ['res-001'],
  },
  {
    id: 'researcher-002',
    tenantId: 'tenant-001',
    firstName: 'Michael',
    lastName: 'Wang',
    email: 'm.wang@swinburne.edu.au',
    profileImageUrl: 'https://ui-avatars.com/api/?name=Michael+Wang&background=764ba2&color=fff',
    title: 'Dr.',
    position: 'Associate Professor',
    department: 'Centre for Astrophysics and Supercomputing',
    institutionId: 'inst-002',
    institutionName: 'Swinburne University of Technology',
    orcidId: '0000-0003-2345-6789',
    expertise: ['Machine Learning', 'Computational Astrophysics', 'Data Science'],
    researchInterests: ['Galaxy formation', 'Stellar evolution', 'AI in astronomy'],
    anzsrcCodes: ['02 - Physical Sciences', '08 - Information and Computing Sciences'],
    publicationCount: 124,
    citationCount: 6789,
    hIndex: 45,
    i10Index: 89,
    isActive: true,
    lastActiveAt: '2024-04-14T09:15:00Z',
    createdAt: '2015-07-15T00:00:00Z',
    updatedAt: '2024-04-14T09:15:00Z',
    collaboratorIds: ['researcher-001', 'researcher-004'],
    resourceIds: ['res-002'],
  },
  {
    id: 'researcher-003',
    tenantId: 'tenant-001',
    firstName: 'David',
    lastName: 'Williams',
    email: 'd.williams@cdu.edu.au',
    profileImageUrl: 'https://ui-avatars.com/api/?name=David+Williams&background=48bb78&color=fff',
    title: 'Prof.',
    position: 'Professor of Environmental Science',
    department: 'Research Institute for the Environment and Livelihoods',
    institutionId: 'inst-003',
    institutionName: 'Charles Darwin University',
    orcidId: '0000-0001-3456-7890',
    expertise: ['Fire Ecology', 'Indigenous Land Management', 'Environmental Policy'],
    researchInterests: ['Cultural burning', 'Savanna ecology', 'Climate adaptation'],
    anzsrcCodes: ['05 - Environmental Sciences', '16 - Studies in Human Society'],
    publicationCount: 156,
    citationCount: 8934,
    hIndex: 52,
    i10Index: 112,
    isActive: true,
    lastActiveAt: '2024-04-13T16:45:00Z',
    createdAt: '2010-01-10T00:00:00Z',
    updatedAt: '2024-04-13T16:45:00Z',
    collaboratorIds: ['researcher-005'],
    resourceIds: ['res-003'],
  },
  {
    id: 'researcher-004',
    tenantId: 'tenant-001',
    firstName: 'Helen',
    lastName: 'Zhang',
    email: 'h.zhang@csiro.au',
    profileImageUrl: 'https://ui-avatars.com/api/?name=Helen+Zhang&background=ed8936&color=fff',
    title: 'Dr.',
    position: 'Principal Research Scientist',
    department: 'Agriculture and Food',
    institutionId: 'inst-004',
    institutionName: 'CSIRO',
    orcidId: '0000-0002-4567-8901',
    expertise: ['Gene Editing', 'Crop Science', 'Plant Pathology'],
    researchInterests: ['CRISPR applications', 'Disease resistance', 'Sustainable agriculture'],
    anzsrcCodes: ['06 - Biological Sciences', '07 - Agricultural and Veterinary Sciences'],
    publicationCount: 98,
    citationCount: 5234,
    hIndex: 41,
    i10Index: 78,
    isActive: true,
    lastActiveAt: '2024-04-15T11:20:00Z',
    createdAt: '2012-09-01T00:00:00Z',
    updatedAt: '2024-04-15T11:20:00Z',
    collaboratorIds: ['researcher-002'],
    resourceIds: ['res-004'],
  },
  {
    id: 'researcher-005',
    tenantId: 'tenant-001',
    firstName: 'Richard',
    lastName: 'Lee',
    email: 'r.lee@unsw.edu.au',
    profileImageUrl: 'https://ui-avatars.com/api/?name=Richard+Lee&background=4299e1&color=fff',
    title: 'Prof.',
    position: 'Professor of Quantum Computing',
    department: 'School of Physics',
    institutionId: 'inst-005',
    institutionName: 'University of New South Wales',
    orcidId: '0000-0003-5678-9012',
    expertise: ['Quantum Algorithms', 'Quantum Chemistry', 'High-Performance Computing'],
    researchInterests: ['Quantum supremacy', 'Drug discovery', 'Quantum error correction'],
    anzsrcCodes: ['02 - Physical Sciences', '08 - Information and Computing Sciences', '03 - Chemical Sciences'],
    publicationCount: 178,
    citationCount: 12456,
    hIndex: 58,
    i10Index: 134,
    isActive: true,
    lastActiveAt: '2024-04-15T10:00:00Z',
    createdAt: '2008-02-15T00:00:00Z',
    updatedAt: '2024-04-15T10:00:00Z',
    collaboratorIds: ['researcher-003', 'researcher-006'],
    resourceIds: ['res-005'],
  },
  {
    id: 'researcher-006',
    tenantId: 'tenant-001',
    firstName: 'Karen',
    lastName: 'White',
    email: 'k.white@unimelb.edu.au',
    profileImageUrl: 'https://ui-avatars.com/api/?name=Karen+White&background=9f7aea&color=fff',
    title: 'Dr.',
    position: 'Senior Lecturer',
    department: 'Faculty of Architecture, Building and Planning',
    institutionId: 'inst-006',
    institutionName: 'University of Melbourne',
    orcidId: '0000-0001-6789-0123',
    expertise: ['Urban Climate', 'Sustainable Design', 'Heat Mitigation'],
    researchInterests: ['Urban heat islands', 'Green infrastructure', 'Climate-responsive cities'],
    anzsrcCodes: ['12 - Built Environment and Design', '05 - Environmental Sciences'],
    publicationCount: 67,
    citationCount: 2345,
    hIndex: 28,
    i10Index: 45,
    isActive: true,
    lastActiveAt: '2024-04-12T15:30:00Z',
    createdAt: '2016-08-01T00:00:00Z',
    updatedAt: '2024-04-12T15:30:00Z',
    collaboratorIds: ['researcher-001'],
    resourceIds: ['res-006'],
  },
  {
    id: 'researcher-007',
    tenantId: 'tenant-001',
    firstName: 'Amanda',
    lastName: 'Foster',
    email: 'a.foster@mq.edu.au',
    profileImageUrl: 'https://ui-avatars.com/api/?name=Amanda+Foster&background=f687b3&color=fff',
    title: 'Dr.',
    position: 'Research Fellow',
    department: 'Department of Linguistics',
    institutionId: 'inst-007',
    institutionName: 'Macquarie University',
    orcidId: '0000-0002-7890-1234',
    expertise: ['Sign Language Processing', 'Computer Vision', 'Accessibility Technology'],
    researchInterests: ['Auslan recognition', 'Deaf community technology', 'Gesture analysis'],
    anzsrcCodes: ['08 - Information and Computing Sciences', '17 - Psychology and Cognitive Sciences'],
    publicationCount: 34,
    citationCount: 890,
    hIndex: 15,
    i10Index: 22,
    isActive: true,
    lastActiveAt: '2024-04-14T14:00:00Z',
    createdAt: '2019-03-15T00:00:00Z',
    updatedAt: '2024-04-14T14:00:00Z',
    collaboratorIds: [],
    resourceIds: ['res-007'],
  },
  {
    id: 'researcher-008',
    tenantId: 'tenant-001',
    firstName: 'Catherine',
    lastName: 'Moore',
    email: 'c.moore@aad.gov.au',
    profileImageUrl: 'https://ui-avatars.com/api/?name=Catherine+Moore&background=38b2ac&color=fff',
    title: 'Prof.',
    position: 'Chief Scientist',
    department: 'Australian Antarctic Division',
    institutionId: 'inst-008',
    institutionName: 'Australian Antarctic Division',
    orcidId: '0000-0003-8901-2345',
    expertise: ['Paleoclimatology', 'Ice Core Analysis', 'Atmospheric Chemistry'],
    researchInterests: ['Climate history', 'Greenhouse gases', 'Antarctic research'],
    anzsrcCodes: ['04 - Earth Sciences', '05 - Environmental Sciences'],
    publicationCount: 234,
    citationCount: 18765,
    hIndex: 67,
    i10Index: 189,
    isActive: true,
    lastActiveAt: '2024-04-15T09:00:00Z',
    createdAt: '2005-06-01T00:00:00Z',
    updatedAt: '2024-04-15T09:00:00Z',
    collaboratorIds: ['researcher-001', 'researcher-003'],
    resourceIds: ['res-012'],
  },
];

const SAMPLE_INSTITUTIONS: Institution[] = [
  {
    id: 'inst-001',
    tenantId: 'tenant-001',
    name: 'Australian Institute of Marine Science',
    shortName: 'AIMS',
    type: 'research_center',
    description: 'Australia\'s tropical marine research agency, providing science for sustainable use and protection of Australia\'s marine estate.',
    logoUrl: '/logos/aims.png',
    websiteUrl: 'https://www.aims.gov.au',
    country: 'Australia',
    state: 'Queensland',
    city: 'Townsville',
    address: 'PMB 3, Townsville MC, QLD 4810',
    email: 'reception@aims.gov.au',
    phone: '+61 7 4753 4444',
    researcherCount: 245,
    resourceCount: 1567,
    citationCount: 89234,
    collaborationCount: 156,
    isPartner: true,
    createdAt: '2020-01-01T00:00:00Z',
    updatedAt: '2024-04-15T00:00:00Z',
  },
  {
    id: 'inst-002',
    tenantId: 'tenant-001',
    name: 'Swinburne University of Technology',
    shortName: 'Swinburne',
    type: 'university',
    description: 'A world-class university creating social and economic impact through science, technology and innovation.',
    logoUrl: '/logos/swinburne.png',
    websiteUrl: 'https://www.swinburne.edu.au',
    country: 'Australia',
    state: 'Victoria',
    city: 'Melbourne',
    address: 'John Street, Hawthorn VIC 3122',
    email: 'info@swinburne.edu.au',
    phone: '+61 3 9214 8000',
    researcherCount: 890,
    resourceCount: 4523,
    citationCount: 156789,
    collaborationCount: 234,
    isPartner: true,
    createdAt: '2020-01-01T00:00:00Z',
    updatedAt: '2024-04-15T00:00:00Z',
  },
  {
    id: 'inst-003',
    tenantId: 'tenant-001',
    name: 'Charles Darwin University',
    shortName: 'CDU',
    type: 'university',
    description: 'A globally connected university in Australia\'s Northern Territory, committed to Indigenous education and tropical knowledge.',
    logoUrl: '/logos/cdu.png',
    websiteUrl: 'https://www.cdu.edu.au',
    country: 'Australia',
    state: 'Northern Territory',
    city: 'Darwin',
    address: 'Ellengowan Drive, Casuarina NT 0810',
    email: 'enquiries@cdu.edu.au',
    phone: '+61 8 8946 6666',
    researcherCount: 456,
    resourceCount: 2134,
    citationCount: 67890,
    collaborationCount: 89,
    isPartner: true,
    createdAt: '2020-01-01T00:00:00Z',
    updatedAt: '2024-04-15T00:00:00Z',
  },
  {
    id: 'inst-004',
    tenantId: 'tenant-001',
    name: 'Commonwealth Scientific and Industrial Research Organisation',
    shortName: 'CSIRO',
    type: 'government',
    description: 'Australia\'s national science agency, solving the greatest challenges through innovative science and technology.',
    logoUrl: '/logos/csiro.png',
    websiteUrl: 'https://www.csiro.au',
    country: 'Australia',
    state: 'Australian Capital Territory',
    city: 'Canberra',
    address: 'Clunies Ross Street, Acton ACT 2601',
    email: 'enquiries@csiro.au',
    phone: '+61 2 6276 6766',
    researcherCount: 5600,
    resourceCount: 23456,
    citationCount: 567890,
    collaborationCount: 789,
    isPartner: true,
    createdAt: '2020-01-01T00:00:00Z',
    updatedAt: '2024-04-15T00:00:00Z',
  },
  {
    id: 'inst-005',
    tenantId: 'tenant-001',
    name: 'University of New South Wales',
    shortName: 'UNSW',
    type: 'university',
    description: 'A powerhouse of cutting-edge research & teaching in the Asia-Pacific, one of Australia\'s leading research & teaching universities.',
    logoUrl: '/logos/unsw.png',
    websiteUrl: 'https://www.unsw.edu.au',
    country: 'Australia',
    state: 'New South Wales',
    city: 'Sydney',
    address: 'UNSW Sydney NSW 2052',
    email: 'info@unsw.edu.au',
    phone: '+61 2 9385 1000',
    researcherCount: 4200,
    resourceCount: 34567,
    citationCount: 789012,
    collaborationCount: 567,
    isPartner: true,
    createdAt: '2020-01-01T00:00:00Z',
    updatedAt: '2024-04-15T00:00:00Z',
  },
  {
    id: 'inst-006',
    tenantId: 'tenant-001',
    name: 'University of Melbourne',
    shortName: 'UniMelb',
    type: 'university',
    description: 'Australia\'s leading university, making distinctive contributions to society in research, teaching and engagement.',
    logoUrl: '/logos/unimelb.png',
    websiteUrl: 'https://www.unimelb.edu.au',
    country: 'Australia',
    state: 'Victoria',
    city: 'Melbourne',
    address: 'Grattan Street, Parkville VIC 3010',
    email: '13melb@unimelb.edu.au',
    phone: '+61 3 9035 5511',
    researcherCount: 4800,
    resourceCount: 45678,
    citationCount: 890123,
    collaborationCount: 678,
    isPartner: true,
    createdAt: '2020-01-01T00:00:00Z',
    updatedAt: '2024-04-15T00:00:00Z',
  },
  {
    id: 'inst-007',
    tenantId: 'tenant-001',
    name: 'Macquarie University',
    shortName: 'Macquarie',
    type: 'university',
    description: 'A research-led university with a commitment to innovative teaching and transformative research.',
    logoUrl: '/logos/macquarie.png',
    websiteUrl: 'https://www.mq.edu.au',
    country: 'Australia',
    state: 'New South Wales',
    city: 'Sydney',
    address: 'Balaclava Road, Macquarie Park NSW 2109',
    email: 'enquiries@mq.edu.au',
    phone: '+61 2 9850 7111',
    researcherCount: 1200,
    resourceCount: 12345,
    citationCount: 234567,
    collaborationCount: 234,
    isPartner: true,
    createdAt: '2020-01-01T00:00:00Z',
    updatedAt: '2024-04-15T00:00:00Z',
  },
  {
    id: 'inst-008',
    tenantId: 'tenant-001',
    name: 'Australian Antarctic Division',
    shortName: 'AAD',
    type: 'government',
    description: 'Leading Australia\'s Antarctic program, advancing Australia\'s strategic, scientific, environmental and economic interests.',
    logoUrl: '/logos/aad.png',
    websiteUrl: 'https://www.antarctica.gov.au',
    country: 'Australia',
    state: 'Tasmania',
    city: 'Hobart',
    address: '203 Channel Highway, Kingston TAS 7050',
    email: 'enquiries@aad.gov.au',
    phone: '+61 3 6232 3666',
    researcherCount: 350,
    resourceCount: 8765,
    citationCount: 345678,
    collaborationCount: 145,
    isPartner: true,
    createdAt: '2020-01-01T00:00:00Z',
    updatedAt: '2024-04-15T00:00:00Z',
  },
];

const SAMPLE_WORKSPACES: Workspace[] = [
  {
    id: 'ws-001',
    tenantId: 'tenant-001',
    name: 'Climate Change Research Hub',
    description: 'Collaborative workspace for climate change researchers across Australian institutions.',
    color: '#667eea',
    iconName: 'Globe',
    ownerId: 'researcher-001',
    ownerName: 'Dr. Sarah Mitchell',
    accessLevel: 'institution',
    memberIds: ['researcher-001', 'researcher-003', 'researcher-006', 'researcher-008'],
    memberCount: 4,
    resourceIds: ['res-001', 'res-006', 'res-012'],
    resourceCount: 3,
    lastActivityAt: '2024-04-15T14:30:00Z',
    createdAt: '2023-06-01T00:00:00Z',
    updatedAt: '2024-04-15T14:30:00Z',
  },
  {
    id: 'ws-002',
    tenantId: 'tenant-001',
    name: 'Quantum Computing Applications',
    description: 'Research workspace focused on practical applications of quantum computing in drug discovery and materials science.',
    color: '#764ba2',
    iconName: 'Atom',
    ownerId: 'researcher-005',
    ownerName: 'Prof. Richard Lee',
    accessLevel: 'team',
    memberIds: ['researcher-005', 'researcher-002'],
    memberCount: 2,
    resourceIds: ['res-002', 'res-005'],
    resourceCount: 2,
    lastActivityAt: '2024-04-14T10:00:00Z',
    createdAt: '2023-09-15T00:00:00Z',
    updatedAt: '2024-04-14T10:00:00Z',
  },
  {
    id: 'ws-003',
    tenantId: 'tenant-001',
    name: 'Agricultural Innovation',
    description: 'Workspace for agricultural research including gene editing, crop science, and sustainable farming practices.',
    color: '#48bb78',
    iconName: 'Leaf',
    ownerId: 'researcher-004',
    ownerName: 'Dr. Helen Zhang',
    accessLevel: 'department',
    memberIds: ['researcher-004'],
    memberCount: 1,
    resourceIds: ['res-004'],
    resourceCount: 1,
    lastActivityAt: '2024-04-15T11:20:00Z',
    createdAt: '2023-07-01T00:00:00Z',
    updatedAt: '2024-04-15T11:20:00Z',
  },
  {
    id: 'ws-004',
    tenantId: 'tenant-001',
    name: 'Accessibility Technology Lab',
    description: 'Research on assistive technologies, sign language recognition, and inclusive design.',
    color: '#f687b3',
    iconName: 'Heart',
    ownerId: 'researcher-007',
    ownerName: 'Dr. Amanda Foster',
    accessLevel: 'public',
    memberIds: ['researcher-007'],
    memberCount: 1,
    resourceIds: ['res-007'],
    resourceCount: 1,
    lastActivityAt: '2024-04-14T14:00:00Z',
    createdAt: '2023-10-01T00:00:00Z',
    updatedAt: '2024-04-14T14:00:00Z',
  },
  {
    id: 'ws-005',
    tenantId: 'tenant-001',
    name: 'Indigenous Knowledge Systems',
    description: 'Collaborative research on traditional ecological knowledge and Indigenous land management practices.',
    color: '#ed8936',
    iconName: 'Flame',
    ownerId: 'researcher-003',
    ownerName: 'Prof. David Williams',
    accessLevel: 'institution',
    memberIds: ['researcher-003'],
    memberCount: 1,
    resourceIds: ['res-003'],
    resourceCount: 1,
    lastActivityAt: '2024-04-13T16:45:00Z',
    createdAt: '2023-05-01T00:00:00Z',
    updatedAt: '2024-04-13T16:45:00Z',
  },
];

const SAMPLE_SEARCH_HISTORY: SearchHistoryItem[] = [
  {
    id: 'search-001',
    tenantId: 'tenant-001',
    userId: 'user-001',
    query: 'climate change datasets 2020-2024 temperature measurements',
    queryType: 'semantic',
    filters: { type: 'dataset', dateFrom: '2020-01-01' },
    resultCount: 156,
    clickedResourceIds: ['res-001', 'res-006', 'res-012'],
    searchedAt: '2024-04-15T14:30:00Z',
  },
  {
    id: 'search-002',
    tenantId: 'tenant-001',
    userId: 'user-001',
    query: 'machine learning astronomy',
    queryType: 'keyword',
    resultCount: 89,
    clickedResourceIds: ['res-002'],
    searchedAt: '2024-04-15T10:15:00Z',
  },
  {
    id: 'search-003',
    tenantId: 'tenant-001',
    userId: 'user-001',
    query: 'quantum computing drug discovery',
    queryType: 'semantic',
    filters: { type: 'patent' },
    resultCount: 34,
    clickedResourceIds: ['res-005'],
    searchedAt: '2024-04-14T16:45:00Z',
  },
  {
    id: 'search-004',
    tenantId: 'tenant-001',
    userId: 'user-001',
    query: 'coral bleaching Great Barrier Reef',
    queryType: 'advanced',
    filters: { institution: 'AIMS', anzsrcCode: '05 - Environmental Sciences' },
    resultCount: 67,
    clickedResourceIds: ['res-001'],
    searchedAt: '2024-04-14T11:30:00Z',
  },
  {
    id: 'search-005',
    tenantId: 'tenant-001',
    userId: 'user-001',
    query: 'CRISPR wheat rust resistance',
    queryType: 'semantic',
    resultCount: 23,
    clickedResourceIds: ['res-004'],
    searchedAt: '2024-04-13T09:00:00Z',
  },
  {
    id: 'search-006',
    tenantId: 'tenant-001',
    userId: 'user-001',
    query: 'urban heat island effect Australia',
    queryType: 'keyword',
    filters: { type: 'dataset' },
    resultCount: 45,
    clickedResourceIds: ['res-006'],
    searchedAt: '2024-04-12T15:20:00Z',
  },
  {
    id: 'search-007',
    tenantId: 'tenant-001',
    userId: 'user-001',
    query: 'Antarctic ice core paleoclimate',
    queryType: 'semantic',
    resultCount: 112,
    clickedResourceIds: ['res-012'],
    searchedAt: '2024-04-11T14:00:00Z',
  },
  {
    id: 'search-008',
    tenantId: 'tenant-001',
    userId: 'user-001',
    query: 'renewable energy grid integration',
    queryType: 'advanced',
    filters: { type: 'report', institution: 'UniSA' },
    resultCount: 28,
    clickedResourceIds: ['res-008'],
    searchedAt: '2024-04-10T10:30:00Z',
  },
];

const SAMPLE_QUALITY_ITEMS: QualityAssuranceItem[] = [
  {
    id: 'qa-001',
    resourceId: 'res-009',
    resourceTitle: 'PhD Thesis: Microplastic Distribution in Australian Coastal Waters',
    resourceType: 'thesis',
    status: 'needs_review',
    metadataCompleteness: 85,
    vocabularyConsistency: true,
    duplicateDetected: false,
    orcidLinked: true,
    doiValid: false,
    issues: [
      { type: 'missing_field', field: 'doi', message: 'DOI not yet assigned', severity: 'medium' },
      { type: 'missing_field', field: 'abstract', message: 'Abstract is missing', severity: 'high' },
    ],
    assignedTo: 'reviewer-001',
    createdAt: '2024-04-15T09:00:00Z',
    updatedAt: '2024-04-15T09:00:00Z',
  },
  {
    id: 'qa-002',
    resourceId: 'res-011',
    resourceTitle: 'Australian Research Infrastructure Investment Strategy 2025-2035',
    resourceType: 'grant_proposal',
    status: 'pending',
    metadataCompleteness: 72,
    vocabularyConsistency: false,
    duplicateDetected: false,
    orcidLinked: false,
    doiValid: false,
    issues: [
      { type: 'missing_field', field: 'methodology', message: 'Research methodology not specified', severity: 'low' },
      { type: 'inconsistent_vocabulary', message: 'ANZSRC codes may not be correctly assigned', severity: 'medium' },
      { type: 'missing_field', field: 'orcidId', message: 'Author ORCID profiles not linked', severity: 'medium' },
    ],
    createdAt: '2024-04-01T08:00:00Z',
    updatedAt: '2024-04-15T10:00:00Z',
  },
  {
    id: 'qa-003',
    resourceId: 'res-001',
    resourceTitle: 'Climate Change Impact on Great Barrier Reef Coral Bleaching Patterns 2020-2024',
    resourceType: 'dataset',
    status: 'passed',
    metadataCompleteness: 98,
    vocabularyConsistency: true,
    duplicateDetected: false,
    orcidLinked: true,
    doiValid: true,
    issues: [],
    reviewedBy: 'reviewer-001',
    reviewedAt: '2024-03-15T14:30:00Z',
    createdAt: '2024-01-10T08:00:00Z',
    updatedAt: '2024-03-15T14:30:00Z',
  },
  {
    id: 'qa-004',
    resourceId: 'res-new-001',
    resourceTitle: 'Draft: Soil Microbiome Analysis Methods',
    resourceType: 'manual',
    status: 'failed',
    metadataCompleteness: 45,
    vocabularyConsistency: false,
    duplicateDetected: true,
    orcidLinked: false,
    doiValid: false,
    issues: [
      { type: 'duplicate', message: 'Similar resource already exists: "Soil Microbiome Protocols v2"', severity: 'high' },
      { type: 'missing_field', field: 'authors', message: 'Author information incomplete', severity: 'high' },
      { type: 'missing_field', field: 'keywords', message: 'Keywords not specified', severity: 'medium' },
      { type: 'missing_field', field: 'anzsrcCodes', message: 'Research classification missing', severity: 'medium' },
    ],
    createdAt: '2024-04-14T11:00:00Z',
    updatedAt: '2024-04-14T11:30:00Z',
  },
];

const SAMPLE_IMAGE_SEARCH_RESULTS: ImageSearchResult[] = [
  {
    resourceId: 'res-001',
    resourceTitle: 'Climate Change Impact on Great Barrier Reef Coral Bleaching Patterns 2020-2024',
    imageUrl: '/images/reef-temperature-chart.png',
    thumbnailUrl: '/thumbnails/reef-chart.png',
    similarity: 0.94,
    matchType: 'chart',
  },
  {
    resourceId: 'res-006',
    resourceTitle: 'Australian Urban Heat Island Effect: Multi-City Temperature Analysis',
    imageUrl: '/images/heat-map-sydney.png',
    thumbnailUrl: '/thumbnails/heat-map.png',
    similarity: 0.87,
    matchType: 'diagram',
  },
  {
    resourceId: 'res-012',
    resourceTitle: 'Antarctic Ice Core Data: 800,000 Year Climate Record',
    imageUrl: '/images/co2-timeline.png',
    thumbnailUrl: '/thumbnails/co2-chart.png',
    similarity: 0.82,
    matchType: 'chart',
  },
];

// ============================================================================
// RESEARCH SERVICE CLASS
// ============================================================================

class ResearchService {
  // Dashboard Stats
  async getDashboardStats(): Promise<ResearchDashboardStats> {
    // Simulate API delay
    await this.delay(300);

    const resourcesByType: Record<ResourceType, number> = {
      article: 3456,
      dataset: 2890,
      software: 567,
      patent: 234,
      lab_tool: 189,
      manual: 456,
      image: 1234,
      thesis: 789,
      report: 345,
      grant_proposal: 123,
    };

    return {
      totalResources: Object.values(resourcesByType).reduce((a, b) => a + b, 0),
      resourcesByType,
      totalResearchers: SAMPLE_RESEARCHERS.length * 125, // Scale up
      totalInstitutions: SAMPLE_INSTITUTIONS.length * 12,
      totalWorkspaces: SAMPLE_WORKSPACES.length * 50,

      recentUploads: 47,
      recentSearches: 1234,
      pendingReviews: 23,

      uploadsThisMonth: 156,
      uploadsLastMonth: 134,
      searchesThisWeek: 5678,

      avgMetadataCompleteness: 87,
      qualityIssuesCount: 12,
    };
  }

  // Resources
  async listResources(params?: ListResourcesParams): Promise<{ data: ResearchResource[]; total: number }> {
    await this.delay(400);

    let filtered = [...SAMPLE_RESOURCES];

    if (params?.search) {
      const search = params.search.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.title.toLowerCase().includes(search) ||
          r.description.toLowerCase().includes(search) ||
          r.keywords.some((k) => k.toLowerCase().includes(search))
      );
    }

    if (params?.type) {
      filtered = filtered.filter((r) => r.type === params.type);
    }

    if (params?.status) {
      filtered = filtered.filter((r) => r.status === params.status);
    }

    if (params?.accessLevel) {
      filtered = filtered.filter((r) => r.accessLevel === params.accessLevel);
    }

    return {
      data: filtered,
      total: filtered.length,
    };
  }

  async getResource(id: string): Promise<ResearchResource | null> {
    await this.delay(200);
    return SAMPLE_RESOURCES.find((r) => r.id === id) || null;
  }

  // Researchers
  async listResearchers(params?: ListResearchersParams): Promise<{ data: Researcher[]; total: number }> {
    await this.delay(400);

    let filtered = [...SAMPLE_RESEARCHERS];

    if (params?.search) {
      const search = params.search.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.firstName.toLowerCase().includes(search) ||
          r.lastName.toLowerCase().includes(search) ||
          r.expertise.some((e) => e.toLowerCase().includes(search)) ||
          r.institutionName.toLowerCase().includes(search)
      );
    }

    if (params?.institution) {
      filtered = filtered.filter((r) => r.institutionName.toLowerCase().includes(params.institution!.toLowerCase()));
    }

    if (params?.expertise) {
      filtered = filtered.filter((r) => r.expertise.some((e) => e.toLowerCase().includes(params.expertise!.toLowerCase())));
    }

    if (params?.sortBy) {
      filtered.sort((a, b) => {
        const order = params.sortOrder === 'desc' ? -1 : 1;
        switch (params.sortBy) {
          case 'name':
            return order * a.lastName.localeCompare(b.lastName);
          case 'citationCount':
            return order * (a.citationCount - b.citationCount);
          case 'hIndex':
            return order * (a.hIndex - b.hIndex);
          case 'publicationCount':
            return order * (a.publicationCount - b.publicationCount);
          default:
            return 0;
        }
      });
    }

    return {
      data: filtered,
      total: filtered.length,
    };
  }

  async getResearcher(id: string): Promise<Researcher | null> {
    await this.delay(200);
    return SAMPLE_RESEARCHERS.find((r) => r.id === id) || null;
  }

  // Institutions
  async listInstitutions(params?: ListInstitutionsParams): Promise<{ data: Institution[]; total: number }> {
    await this.delay(400);

    let filtered = [...SAMPLE_INSTITUTIONS];

    if (params?.search) {
      const search = params.search.toLowerCase();
      filtered = filtered.filter(
        (i) => i.name.toLowerCase().includes(search) || i.shortName.toLowerCase().includes(search) || i.city.toLowerCase().includes(search)
      );
    }

    if (params?.type) {
      filtered = filtered.filter((i) => i.type === params.type);
    }

    if (params?.isPartner !== undefined) {
      filtered = filtered.filter((i) => i.isPartner === params.isPartner);
    }

    return {
      data: filtered,
      total: filtered.length,
    };
  }

  async getInstitution(id: string): Promise<Institution | null> {
    await this.delay(200);
    return SAMPLE_INSTITUTIONS.find((i) => i.id === id) || null;
  }

  // Workspaces
  async listWorkspaces(): Promise<{ data: Workspace[]; total: number }> {
    await this.delay(300);
    return {
      data: SAMPLE_WORKSPACES,
      total: SAMPLE_WORKSPACES.length,
    };
  }

  async getWorkspace(id: string): Promise<Workspace | null> {
    await this.delay(200);
    return SAMPLE_WORKSPACES.find((w) => w.id === id) || null;
  }

  // Search History
  async listSearchHistory(): Promise<{ data: SearchHistoryItem[]; total: number }> {
    await this.delay(300);
    return {
      data: SAMPLE_SEARCH_HISTORY.sort((a, b) => new Date(b.searchedAt).getTime() - new Date(a.searchedAt).getTime()),
      total: SAMPLE_SEARCH_HISTORY.length,
    };
  }

  async deleteSearchHistoryItem(id: string): Promise<void> {
    await this.delay(200);
    // In real implementation, this would delete from database
  }

  async clearSearchHistory(): Promise<void> {
    await this.delay(300);
    // In real implementation, this would clear all history
  }

  // Image Search
  async searchByImage(_file: File): Promise<ImageSearchResult[]> {
    await this.delay(1500); // Simulate image processing
    return SAMPLE_IMAGE_SEARCH_RESULTS;
  }

  // Citations
  async generateCitation(resourceId: string, format: Citation['format']): Promise<Citation> {
    await this.delay(500);

    const resource = SAMPLE_RESOURCES.find((r) => r.id === resourceId);
    if (!resource) {
      throw new Error('Resource not found');
    }

    const year = resource.publicationDate ? new Date(resource.publicationDate).getFullYear().toString() : new Date().getFullYear().toString();

    const formatCitation = (format: Citation['format']): string => {
      const authors = resource.authors.join(', ');
      switch (format) {
        case 'APA':
          return `${authors} (${year}). ${resource.title}. ${resource.doi ? `https://doi.org/${resource.doi}` : ''}`;
        case 'MLA':
          return `${authors}. "${resource.title}." ${year}. ${resource.doi ? `doi:${resource.doi}` : ''}`;
        case 'Chicago':
          return `${authors}. "${resource.title}." ${year}. ${resource.doi ? `https://doi.org/${resource.doi}` : ''}`;
        case 'Harvard':
          return `${authors} ${year}, '${resource.title}', ${resource.doi ? `doi: ${resource.doi}` : ''}`;
        case 'IEEE':
          return `${authors}, "${resource.title}," ${year}. ${resource.doi ? `doi: ${resource.doi}` : ''}`;
        case 'Vancouver':
          return `${authors}. ${resource.title}. ${year}. ${resource.doi ? `DOI: ${resource.doi}` : ''}`;
        default:
          return `${authors} (${year}). ${resource.title}.`;
      }
    };

    return {
      id: `citation-${Date.now()}`,
      resourceId,
      resourceTitle: resource.title,
      format,
      formattedCitation: formatCitation(format),
      authors: resource.authors,
      year,
      doi: resource.doi,
      createdAt: new Date().toISOString(),
    };
  }

  // Quality Assurance
  async listQualityItems(): Promise<{ data: QualityAssuranceItem[]; total: number }> {
    await this.delay(400);
    return {
      data: SAMPLE_QUALITY_ITEMS,
      total: SAMPLE_QUALITY_ITEMS.length,
    };
  }

  async getQualityItem(id: string): Promise<QualityAssuranceItem | null> {
    await this.delay(200);
    return SAMPLE_QUALITY_ITEMS.find((q) => q.id === id) || null;
  }

  // Settings
  async getSettings(): Promise<ResearchSettings> {
    await this.delay(200);
    return {
      notifications: {
        newResearchAlerts: true,
        citationAlerts: true,
        collaborationRequests: true,
        weeklyDigest: false,
      },
      researchInterests: ['Climate Change', 'Marine Biology', 'Data Science'],
      preferredCitationFormat: 'APA',
      defaultAccessLevel: 'institution',
      autoTagging: true,
    };
  }

  async updateSettings(settings: Partial<ResearchSettings>): Promise<ResearchSettings> {
    await this.delay(300);
    // In real implementation, this would save to database
    return {
      notifications: {
        newResearchAlerts: true,
        citationAlerts: true,
        collaborationRequests: true,
        weeklyDigest: false,
      },
      researchInterests: ['Climate Change', 'Marine Biology', 'Data Science'],
      preferredCitationFormat: 'APA',
      defaultAccessLevel: 'institution',
      autoTagging: true,
      ...settings,
    };
  }

  // Semantic Search (simulated)
  async semanticSearch(query: string): Promise<ResearchResource[]> {
    await this.delay(800);

    // Simulate semantic search by keyword matching
    const queryWords = query.toLowerCase().split(' ');
    return SAMPLE_RESOURCES.filter((r) => {
      const searchText = `${r.title} ${r.description} ${r.keywords.join(' ')} ${r.authors.join(' ')}`.toLowerCase();
      return queryWords.some((word) => searchText.includes(word));
    }).slice(0, 10);
  }

  // Get trending topics
  async getTrendingTopics(): Promise<{ topic: string; count: number }[]> {
    await this.delay(300);
    return [
      { topic: 'Climate Change', count: 1234 },
      { topic: 'Machine Learning', count: 987 },
      { topic: 'Quantum Computing', count: 654 },
      { topic: 'Gene Editing', count: 543 },
      { topic: 'Renewable Energy', count: 432 },
      { topic: 'Marine Biology', count: 321 },
      { topic: 'Indigenous Knowledge', count: 234 },
      { topic: 'Antarctic Research', count: 198 },
    ];
  }

  // Get recent activity
  async getRecentActivity(): Promise<
    {
      id: string;
      type: 'upload' | 'citation' | 'download' | 'collaboration';
      description: string;
      timestamp: string;
    }[]
  > {
    await this.delay(300);
    return [
      {
        id: 'act-001',
        type: 'upload',
        description: 'New dataset uploaded: Antarctic Ice Core Data',
        timestamp: '2024-04-15T14:30:00Z',
      },
      {
        id: 'act-002',
        type: 'citation',
        description: 'Your paper was cited by Dr. Chen',
        timestamp: '2024-04-15T12:15:00Z',
      },
      {
        id: 'act-003',
        type: 'download',
        description: 'Climate dataset downloaded 50 times today',
        timestamp: '2024-04-15T10:00:00Z',
      },
      {
        id: 'act-004',
        type: 'collaboration',
        description: 'New collaboration request from CSIRO',
        timestamp: '2024-04-14T16:45:00Z',
      },
      {
        id: 'act-005',
        type: 'upload',
        description: 'Software update: ML Astronomy Framework v2.1',
        timestamp: '2024-04-14T11:30:00Z',
      },
    ];
  }

  // Helper method
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export const researchService = new ResearchService();
